import { useState } from "react";
import { BanknotesIcon } from "@heroicons/react/24/outline";
import { economyApi } from "../../../api/economy";
import { useEconomyText, useRemote } from "../accounting/economy/shared";
import {
  bookedComparableTotal,
  estimatedOperatingCost,
  vehicleCostDifference,
} from "../accounting/economy/vehicleCostTotals";

const selectClass =
  "mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500";

function Fact({ label, value, warn = false }) {
  return (
    <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="text-sm text-gray-500">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${warn ? "text-amber-700" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}

export function VehicleCostSection({ vehicleId }) {
  const { t, money, monthName } = useEconomyText();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const estimates = useRemote(
    (signal) => economyApi.estimates({ year, through_month: month }, signal),
    [year, month]
  );
  const postings = useRemote(
    (signal) => economyApi.vehiclePostings({ vehicle_id: vehicleId, year, month, page: 1 }, signal),
    [vehicleId, year, month]
  );

  if (!vehicleId) return null;
  if (estimates.error?.httpStatus === 403 || postings.error?.httpStatus === 403) return null;

  const vehicle = estimates.data?.vehicles?.find((row) => String(row.id) === String(vehicleId));
  const estimate = vehicle?.estimate;
  const estimated = estimatedOperatingCost(estimate);
  const booked = bookedComparableTotal(postings.data);
  const difference = vehicleCostDifference(estimated, booked);
  const monthKilometers = vehicle?.months?.find((row) => Number(row.month) === Number(month))?.kilometers;
  const kilometers = estimate?.kilometers != null ? estimate.kilometers : monthKilometers;
  const costPerKm = estimate?.cost_per_km;
  const showMissing = Boolean(estimates.data) && !estimates.loading && !estimate?.available;
  const failed = estimates.error || postings.error;

  const facts = [
    { key: "operating_cost", value: money(estimated) },
    { key: "booked", value: money(booked) },
    { key: "difference", value: money(difference), warn: difference > 0 },
  ];
  if (costPerKm != null) facts.push({ key: "cost_per_km", value: money(costPerKm, 2) });
  if (kilometers != null) facts.push({ key: "kilometers", value: `${money(kilometers)} km` });

  const years = [];
  for (let value = 2000; value <= 2200; value += 1) years.push(value);

  return (
    <section className="mt-5 shadow sm:overflow-hidden sm:rounded-md" aria-labelledby="vehicle-cost-heading">
      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <BanknotesIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
              <h3 id="vehicle-cost-heading" className="text-lg font-medium leading-6 text-gray-900">
                {t("vehicleCosts")}
              </h3>
            </div>
            <p className="mt-1 text-sm text-gray-500">{t("vehicleIntro")}</p>
          </div>
          <div className="flex gap-3">
            <label className="block text-sm">
              <span className="block font-medium text-gray-700">{t("year")}</span>
              <select aria-label={t("year")} className={selectClass} value={year} onChange={(event) => setYear(Number(event.target.value))}>
                {years.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-gray-700">{t("month")}</span>
              <select aria-label={t("month")} className={selectClass} value={month} onChange={(event) => setMonth(Number(event.target.value))}>
                {Array.from({ length: 12 }, (_, index) => index + 1).map((value) => (
                  <option key={value} value={value}>{monthName(value)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {(estimates.loading || postings.loading) && (
          <p role="status" className="text-sm text-gray-500">{t("loading")}</p>
        )}
        {failed && (
          <div role="alert" className="text-sm text-gray-700">
            <p>{t("loadFailed")}</p>
            <button
              type="button"
              onClick={() => { estimates.refresh(); postings.refresh(); }}
              className="mt-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {t("retry")}
            </button>
          </div>
        )}
        {showMissing && <p className="text-sm text-amber-700">{t("missing")}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {facts.map((fact) => (
            <Fact key={fact.key} label={t(fact.key)} value={fact.value} warn={fact.warn} />
          ))}
        </div>
      </div>
    </section>
  );
}
